import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, concat, defer, forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AccessService } from '../../../core/services/access.service';
import { AdminAcademicAppointmentService } from '../../../core/services/academic-appointments/admin-academic-appointment.service';
import { SchedulingSubject } from '../../../core/interfaces/academic-appointment';
import { SaveInspectionScope } from '../../../core/interfaces/inspection-scope';
import { InspectionScopeEditorComponent } from '../../../components/ui/inspection-scope/inspection-scope-editor.component';
import {
  AccessCatalog,
  AccessModule,
  AccessPermission,
  AccessProfile,
  CatalogSync,
  StaffAccount,
} from '../../../core/interfaces/access';
import { SwalService } from '../../../core/services/utils/swal.service';
import { matchesSearch } from '../../../core/utils/search-text';
import { LocalizedLabelService } from '../../../core/services/utils/localized-label.service';
import { ErrorStateComponent } from '../../../components/ui/error-state/error-state.component';
import { PermissionPickerComponent } from '../../../components/ui/permission-picker/permission-picker.component';

type Tab = 'profiles' | 'accounts';
type AccountFilter = 'all' | 'admin' | 'professor';
/** Les trois écritures du dialogue d'un compte, dans l'ordre où elles partent. */
type AssignStep = 'profiles' | 'scope' | 'permissions';

/** D'où un compte tient une permission qu'il a déjà, hors attribution individuelle. */
interface PermissionOrigin {
  /** Libellé affiché tel quel (nom d'un profil) — ou clé i18n si `translate`. */
  label: string;
  translate: boolean;
}

interface Draft {
  /** Nul tant que le profil n'est pas créé. */
  key: string | null;
  label: string;
  description: string;
  permissions: Set<string>;
}


/**
 * Profils d'accès et leur attribution — SKILL2ERA-129.
 *
 * <p>Le superviseur compose des profils à partir du catalogue de permissions,
 * puis les attribue aux administrateurs et aux professeurs. Chaque profil est
 * un rôle Keycloak : l'écran ne garde rien, il relit le realm après chaque
 * geste.</p>
 *
 * <p><b>Ce qui se modifie, c'est le serveur qui le dit</b> (`editable`) : un
 * profil, et le rôle de base `admin` — le socle de tout administrateur. Le
 * superviseur, `professor` et les rôles montés à la main dans la console se
 * <b>lisent</b> seulement : l'écran ne propose pas un geste que le serveur
 * refuserait.</p>
 *
 * <p><b>Un accès pour une seule personne</b> se donne dans le dialogue du
 * compte, par des permissions individuelles : elles s'ajoutent à ce que le
 * compte tient déjà de son rôle de base et de ses profils, sans obliger à
 * créer un profil pour lui.</p>
 */
@Component({
  selector: 'app-admin-access',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ErrorStateComponent, InspectionScopeEditorComponent, PermissionPickerComponent],
  templateUrl: './access.component.html',
  styleUrl: './access.component.css',
})
export class AccessComponent implements OnInit {
  private readonly service = inject(AccessService);
  private readonly referential = inject(AdminAcademicAppointmentService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly labels = inject(LocalizedLabelService);

  @ViewChild('assignDialog') private assignDialog?: ElementRef<HTMLDialogElement>;

  readonly tab = signal<Tab>('profiles');
  readonly loading = signal(true);
  readonly errored = signal(false);
  /** Message du serveur à l'échec du chargement — il nomme le droit Keycloak manquant. */
  readonly errorDetail = signal('');
  readonly saving = signal(false);

  readonly catalog = signal<AccessCatalog>({ modules: [], missingInKeycloak: [] });
  readonly profiles = signal<AccessProfile[]>([]);
  readonly accounts = signal<StaffAccount[]>([]);

  // ── Profils ───────────────────────────────────────────────────────

  readonly draft = signal<Draft | null>(null);

  readonly selected = computed(() => {
    const key = this.draft()?.key;
    return key ? this.profiles().find((p) => p.key === key) ?? null : null;
  });

  /**
   * Lecture seule, sauf pour ce que le serveur déclare réglable — ou pour un
   * profil en cours de création.
   */
  readonly readOnly = computed(() => {
    const d = this.draft();
    if (!d) return true;
    return d.key !== null && !this.isEditable(this.selected());
  });

  /**
   * Un rôle de base ouvert au réglage : ses permissions se cochent, mais ni son
   * nom ni son existence ne se décident ici — tout l'espace s'ouvre par lui.
   */
  readonly editingBase = computed(() => !this.readOnly() && this.selected()?.kind === 'base');

  readonly dirty = computed(() => {
    const d = this.draft();
    if (!d || this.readOnly()) return false;
    const p = this.selected();
    if (!p) return d.label.trim().length > 0;
    const permissionsChanged = !sameSet(d.permissions, new Set(p.permissions));
    if (p.kind === 'base') return permissionsChanged;
    return d.label !== p.label
      || (d.description || '') !== (p.description || '')
      || permissionsChanged;
  });

  readonly canSave = computed(() =>
    this.dirty() && !this.saving() && (this.draft()?.label.trim().length ?? 0) > 0);

  readonly permissionIndex = computed(() => {
    const index = new Map<string, AccessPermission>();
    for (const m of this.catalog().modules) for (const p of m.permissions) index.set(p.key, p);
    return index;
  });

  // ── Synchronisation du catalogue ──────────────────────────────────

  readonly syncing = signal(false);
  /** Ce que la dernière synchronisation a fait — affiché jusqu'à la suivante. */
  readonly syncResult = signal<CatalogSync | null>(null);
  /** Le refus de Keycloak, tel que le serveur le formule : il nomme le droit manquant. */
  readonly syncError = signal('');

  // ── Comptes ───────────────────────────────────────────────────────

  readonly filter = signal<AccountFilter>('all');
  readonly search = signal('');

  readonly visibleAccounts = computed(() => {
    const q = this.search();
    const f = this.filter();
    return this.accounts().filter((a) => {
      const isProf = a.baseRoles.includes('professor');
      const isAdmin = a.baseRoles.includes('supervisor') || a.baseRoles.includes('admin');
      if (f === 'professor' && !isProf) return false;
      if (f === 'admin' && !isAdmin) return false;
      return matchesSearch(q, a.fullName, a.email);
    });
  });

  /** Les profils attribuables : ceux composés à l'écran. */
  readonly assignable = computed(() => this.profiles().filter((p) => p.kind === 'profile'));

  readonly editing = signal<StaffAccount | null>(null);
  readonly choice = signal<Set<string>>(new Set());
  /** Les permissions individuelles cochées dans le dialogue — celles du compte, et de lui seul. */
  readonly direct = signal<Set<string>>(new Set());

  /**
   * Le périmètre d'inspection, composé avec le profil qui inspecte.
   *
   * <p>Il n'apparaît que si l'un des profils choisis porte `sessions.inspect` :
   * sans inspection, il n'y a rien à borner. Il reste en base quand le profil
   * est retiré — prêt pour une réattribution.</p>
   */
  readonly scope = signal<SaveInspectionScope>({ all: false, items: [] });
  /** Le périmètre enregistré a été lu : sans lui, enregistrer écraserait l'existant par du vide. */
  readonly scopeLoaded = signal(false);
  readonly referentialSubjects = signal<SchedulingSubject[]>([]);
  readonly scopeVisible = computed(() =>
    [...this.choice()].some((key) => this.profiles().find((p) => p.key === key)?.permissions.includes('sessions.inspect')));

  /** Ce que le rôle de base du compte en cours lui donne déjà. */
  private readonly basePermissions = computed(() => {
    const account = this.editing();
    if (!account) return new Set<string>();
    return new Set(account.basePermissions ?? this.profiles()
      .filter((p) => p.kind === 'base' && account.baseRoles.includes(p.key))
      .flatMap((p) => p.permissions));
  });

  /**
   * D'où le compte tient chaque permission, hors attribution individuelle.
   *
   * <p>Les profils se lisent dans le <b>choix en cours</b> du dialogue, pas
   * dans ce qui est enregistré : cocher un profil au-dessus fait aussitôt
   * apparaître ce qu'il apporte, et le décocher rend ses permissions
   * attribuables à titre individuel.</p>
   */
  readonly origins = computed(() => {
    const account = this.editing();
    const map = new Map<string, PermissionOrigin[]>();
    if (!account) return map;

    const add = (key: string, origin: PermissionOrigin) => {
      const list = map.get(key);
      if (list) list.push(origin); else map.set(key, [origin]);
    };

    for (const key of this.basePermissions()) add(key, { label: 'ACCESS.ORIGIN.base', translate: true });
    for (const profileKey of [...this.choice()].sort()) {
      const profile = this.profiles().find((p) => p.key === profileKey);
      for (const key of profile?.permissions ?? []) add(key, { label: profile!.label, translate: false });
    }
    for (const key of account.legacyPermissions ?? []) add(key, { label: 'ACCESS.ORIGIN.legacy', translate: true });
    return map;
  });

  /** Ce que le compte gagnerait en plus de ses rôles de base — par ses profils ou à titre individuel. */
  readonly gains = computed(() => {
    if (!this.editing()) return [];
    const base = this.basePermissions();
    const keys = new Set<string>();
    for (const key of this.choice()) {
      for (const perm of this.profiles().find((p) => p.key === key)?.permissions ?? []) {
        if (!base.has(perm)) keys.add(perm);
      }
    }
    for (const perm of this.direct()) if (!base.has(perm)) keys.add(perm);
    return [...keys].sort();
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.errored.set(false);

    forkJoin({
      catalog: this.service.catalog(),
      profiles: this.service.profiles(),
      accounts: this.service.accounts(),
    }).subscribe({
      next: ({ catalog, profiles, accounts }) => {
        this.catalog.set(catalog?.data ?? { modules: [], missingInKeycloak: [] });
        this.profiles.set(profiles?.data ?? []);
        this.accounts.set(accounts?.data ?? []);
        this.loading.set(false);

        // Une édition en cours — profil modifié ou nouveau profil — survit à
        // la relecture : on ne remplace le brouillon que s'il n'a rien à perdre.
        if (this.dirty()) return;
        const keep = this.draft()?.key;
        const first = this.profiles().find((p) => p.key === keep)
          ?? this.profiles().find((p) => p.kind === 'profile')
          ?? this.profiles()[0];
        if (first) this.select(first);
      },
      error: (err) => {
        this.errorDetail.set((err as { error?: { message?: string } })?.error?.message ?? '');
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  /** Libellé dans la langue courante, avec le repli que le service connaît. */
  label(item: AccessModule | AccessPermission): string {
    return this.labels.pickPrefixed(item as unknown as Record<string, unknown>, 'label');
  }

  permissionLabel(key: string): string {
    const p = this.permissionIndex().get(key);
    return p ? this.label(p) : key;
  }

  isEnforced(key: string): boolean {
    return this.permissionIndex().get(key)?.enforced ?? false;
  }

  /**
   * Le drapeau du serveur fait foi. Le repli sur le genre ne sert qu'à un
   * serveur d'avant ce drapeau, pour qui seul un profil se modifiait.
   */
  isEditable(profile: AccessProfile | null | undefined): boolean {
    if (!profile) return false;
    return profile.editable ?? profile.kind === 'profile';
  }

  // ── Synchronisation du catalogue ──────────────────────────────────

  /** Crée dans Keycloak ce qui manque au catalogue, puis relit catalogue et profils. */
  syncCatalog(): void {
    if (this.syncing()) return;
    this.syncing.set(true);
    this.syncError.set('');

    this.service.syncCatalog().subscribe({
      next: (res) => {
        this.syncing.set(false);
        this.syncResult.set(res?.data ?? { createdPermissions: [], upgradedRoles: [] });
        this.load();
      },
      error: (err) => {
        this.syncing.set(false);
        this.syncError.set(this.messageFrom(err));
      },
    });
  }

  // ── Édition d'un profil ───────────────────────────────────────────

  select(profile: AccessProfile): void {
    this.draft.set({
      key: profile.key,
      label: profile.label,
      description: profile.description ?? '',
      permissions: new Set(profile.permissions),
    });
  }

  startNew(): void {
    this.draft.set({ key: null, label: '', description: '', permissions: new Set() });
  }

  patchDraft(changes: Partial<Draft>): void {
    this.draft.update((d) => (d ? { ...d, ...changes } : d));
  }

  isChecked(key: string): boolean {
    return this.draft()?.permissions.has(key) ?? false;
  }

  /**
   * Une permission réservée à qui a une fiche professeur n'ouvre rien à un
   * administrateur : le serveur refuse de l'ajouter au rôle de base. Déjà
   * cochée (composée dans la console), elle reste décochable.
   */
  ownClaimLocked(perm: AccessPermission): boolean {
    return this.editingBase() && !!perm.ownClaim && !this.isChecked(perm.key);
  }

  /** La même règle, sous la forme qu'attend le sélecteur partagé. */
  readonly isOwnClaimLocked = (perm: AccessPermission): boolean => this.ownClaimLocked(perm);

  toggle(key: string): void {
    if (this.readOnly()) return;
    const perm = this.permissionIndex().get(key);
    if (perm && this.ownClaimLocked(perm)) return;
    this.draft.update((d) => {
      if (!d) return d;
      const next = new Set(d.permissions);
      next.has(key) ? next.delete(key) : next.add(key);
      return { ...d, permissions: next };
    });
  }

  moduleCount(m: AccessModule): number {
    return m.permissions.filter((p) => this.isChecked(p.key)).length;
  }

  toggleModule(m: AccessModule): void {
    if (this.readOnly()) return;
    const all = this.moduleCount(m) === m.permissions.length;
    this.draft.update((d) => {
      if (!d) return d;
      const next = new Set(d.permissions);
      for (const p of m.permissions) {
        if (all) next.delete(p.key);
        else if (!this.ownClaimLocked(p)) next.add(p.key);
      }
      return { ...d, permissions: next };
    });
  }

  /** Toutes les permissions de lecture — le point de départ d'un profil de consultation. */
  allRead(): void {
    if (this.readOnly()) return;
    const reads = this.catalog().modules
      .flatMap((m) => m.permissions)
      .filter((p) => !this.ownClaimLocked(p))
      .map((p) => p.key)
      .filter((k) => /\.read(\.|$)/.test(k) && !k.endsWith('.own'));
    this.patchDraft({ permissions: new Set(reads) });
  }

  clearAll(): void {
    if (this.readOnly()) return;
    this.patchDraft({ permissions: new Set() });
  }

  async save(): Promise<void> {
    const d = this.draft();
    if (!d || !this.canSave()) return;

    if (this.editingBase()) {
      await this.saveBaseRole(d);
      return;
    }

    const dto = {
      label: d.label.trim(),
      description: d.description.trim() || null,
      permissions: [...d.permissions].sort(),
    };

    this.saving.set(true);
    const call = d.key ? this.service.updateProfile(d.key, dto) : this.service.createProfile(dto);

    call.subscribe({
      next: (res) => {
        this.saving.set(false);
        if (res?.data) this.draft.set({ ...d, key: res.data.key });
        this.swal.success(this.translate.instant('ACCESS.SAVED'));
        this.load();
      },
      error: (err) => {
        this.saving.set(false);
        this.swal.error(this.messageFrom(err));
      },
    });
  }

  /**
   * Le socle de tous les administrateurs : l'enregistrement les touche tous,
   * et se confirme en nommant combien. Seules les clés du catalogue partent —
   * le serveur ne remplace qu'elles, et refuserait une clé étrangère composée
   * dans la console.
   */
  private async saveBaseRole(d: Draft): Promise<void> {
    const role = this.selected();
    if (!role || !d.key) return;

    const ok = await this.swal.confirmAsync(this.translate.instant('ACCESS.BASE_CONFIRM', {
      label: role.label,
      count: role.accountCount ?? 0,
    }));
    if (!ok) return;

    const permissions = [...d.permissions].filter((k) => this.permissionIndex().has(k)).sort();

    this.saving.set(true);
    this.service.updateBaseRole(d.key, permissions).subscribe({
      next: () => {
        this.saving.set(false);
        // Le brouillon n'a plus rien à perdre : la relecture le remplace par l'état du realm.
        this.draft.set({ ...d, permissions: new Set(role.permissions) });
        this.swal.success(this.translate.instant('ACCESS.BASE_SAVED'));
        this.load();
      },
      error: (err) => {
        this.saving.set(false);
        this.swal.error(this.messageFrom(err));
      },
    });
  }

  async remove(): Promise<void> {
    const p = this.selected();
    if (!p || p.kind !== 'profile') return;

    if ((p.accountCount ?? 0) > 0) {
      this.swal.error(this.translate.instant('ACCESS.DELETE_IN_USE', { count: p.accountCount }));
      return;
    }

    const ok = await this.swal.confirmAsync(this.translate.instant('ACCESS.DELETE_CONFIRM', { label: p.label }));
    if (!ok) return;

    this.service.deleteProfile(p.key).subscribe({
      next: () => {
        this.draft.set(null);
        this.swal.success(this.translate.instant('ACCESS.DELETED'));
        this.load();
      },
      error: (err) => this.swal.error(this.messageFrom(err)),
    });
  }

  // ── Attribution ───────────────────────────────────────────────────

  profileLabel(key: string): string {
    return this.profiles().find((p) => p.key === key)?.label ?? key;
  }

  isSupervisor(a: StaffAccount): boolean {
    return a.baseRoles.includes('supervisor');
  }

  openAssign(account: StaffAccount): void {
    this.editing.set(account);
    this.choice.set(new Set(account.profiles));
    this.direct.set(new Set(account.directPermissions ?? []));
    this.scope.set({ all: false, items: [] });
    this.scopeLoaded.set(false);
    this.loadScope(account);
    const el = this.assignDialog?.nativeElement;
    // `<dialog>` + showModal() : peint dans la couche supérieure, hors de la
    // coquille qui casse `position: fixed`.
    if (el && !el.open) el.showModal();
  }

  toggleChoice(key: string): void {
    this.choice.update((c) => {
      const next = new Set(c);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  // ── Permissions individuelles ─────────────────────────────────────

  isProfessor(a: StaffAccount): boolean {
    return a.baseRoles.includes('professor');
  }

  originsOf(key: string): PermissionOrigin[] {
    return this.origins().get(key) ?? [];
  }

  /** Cochée : le compte l'a, d'où qu'elle vienne. */
  hasPermission(key: string): boolean {
    return this.direct().has(key) || this.origins().has(key);
  }

  /**
   * Non modifiable ici : le compte la tient déjà d'ailleurs (elle se retire à
   * la source), ou elle ne lui ouvrirait rien faute de fiche professeur. Une
   * permission individuelle déjà posée reste toujours décochable.
   */
  directLocked(perm: AccessPermission): boolean {
    if (this.direct().has(perm.key)) return false;
    return this.origins().has(perm.key) || this.ownClaimUseless(perm);
  }

  /** Réservée à qui a une fiche professeur, pour un compte qui n'en a pas. */
  ownClaimUseless(perm: AccessPermission): boolean {
    const account = this.editing();
    return !!perm.ownClaim && !!account && !this.isProfessor(account);
  }

  toggleDirect(perm: AccessPermission): void {
    if (this.directLocked(perm)) return;
    this.direct.update((d) => {
      const next = new Set(d);
      next.has(perm.key) ? next.delete(perm.key) : next.add(perm.key);
      return next;
    });
  }

  directCount(m: AccessModule): number {
    return m.permissions.filter((p) => this.direct().has(p.key)).length;
  }

  heldCount(m: AccessModule): number {
    return m.permissions.filter((p) => this.hasPermission(p.key)).length;
  }

  closeAssign(): void {
    this.assignDialog?.nativeElement.close();
  }

  onAssignClosed(): void {
    this.editing.set(null);
  }

  confirmAssign(): void {
    const account = this.editing();
    if (!account) return;

    // Trois écritures, l'une après l'autre : les profils, puis le périmètre
    // qui en dépend — un profil refusé ne laisse pas un périmètre orphelin —,
    // puis les permissions individuelles. Aucune transaction ne les relie
    // (ce sont trois appels à Keycloak et à la base) : si l'une échoue, les
    // précédentes sont faites, et l'écran doit dire lesquelles.
    const steps: { name: AssignStep; run: () => Observable<unknown> }[] = [
      { name: 'profiles', run: () => this.service.setAccountProfiles(account.userId, [...this.choice()].sort()) },
    ];
    if (this.scopeVisible() && this.scopeLoaded()) {
      const scope = this.scope();
      steps.push({ name: 'scope', run: () => this.service.setInspectionScope(account.userId, scope) });
    }
    const direct = [...this.direct()].sort();
    // Rien à écrire si rien n'a bougé : un appel à Keycloak et une ligne de
    // journal de moins pour un simple changement de profil.
    if (!sameSet(new Set(direct), new Set(account.directPermissions ?? []))) {
      steps.push({ name: 'permissions', run: () => this.service.setAccountPermissions(account.userId, direct) });
    }

    const saved: AssignStep[] = [];
    let running: AssignStep = 'profiles';

    this.saving.set(true);
    concat(...steps.map((step) => defer(() => {
      running = step.name;
      return step.run();
    }).pipe(tap(() => saved.push(step.name))))).subscribe({
      complete: () => {
        this.saving.set(false);
        this.closeAssign();
        this.swal.success(this.translate.instant('ACCESS.ASSIGNED'));
        this.load();
      },
      error: (err) => {
        this.saving.set(false);
        // Une écriture a pu passer avant qu'une autre n'échoue : l'écran dit
        // ce qui est enregistré, ce qui ne l'est pas, et relit l'état réel.
        this.swal.error(saved.length === 0
          ? this.messageFrom(err)
          : this.translate.instant('ACCESS.PARTIAL_FAILED', {
              saved: saved.map((s) => this.translate.instant('ACCESS.STEP.' + s)).join(', '),
              failed: this.translate.instant('ACCESS.STEP.' + running),
              message: this.messageFrom(err),
            }));
        this.load();
      },
    });
  }

  onScopeChange(value: SaveInspectionScope): void {
    this.scope.set(value);
  }

  /** Numéro de la dernière lecture de périmètre : une réponse en retard ne compte plus. */
  private scopeRequest = 0;

  /** Le périmètre enregistré du compte — à chaque ouverture du dialogue. */
  private loadScope(account: StaffAccount): void {
    this.ensureReferential();
    const request = ++this.scopeRequest;
    this.service.inspectionScope(account.userId).subscribe({
      next: (res) => {
        // Ouvrir A (lent) puis B (rapide) : la réponse de A arriverait sur le
        // dialogue de B, et Enregistrer écrirait le périmètre de A sur B.
        if (request !== this.scopeRequest) return;
        const saved = res?.data;
        this.scope.set({
          all: saved?.all === true,
          items: (saved?.items ?? []).map((i) => ({ learningPathId: i.learningPathId, subjectId: i.subjectId })),
        });
        this.scopeLoaded.set(true);
      },
      // Périmètre illisible : l'éditeur reste visible mais rien ne partira à
      // l'enregistrement — on n'écrase pas ce qu'on n'a pas pu lire.
      error: () => { if (request === this.scopeRequest) this.scopeLoaded.set(false); },
    });
  }

  private referentialLoaded = false;

  /** Les niveaux et leurs matières ne changent pas d'un compte à l'autre : une seule lecture par écran. */
  private ensureReferential(): void {
    if (this.referentialLoaded) return;
    this.referentialLoaded = true;
    this.referential.options().subscribe({
      next: (res) => this.referentialSubjects.set(res?.data?.subjects ?? []),
      // Sans référentiel, l'éditeur dit qu'il n'a rien à proposer ; le reste
      // du dialogue reste utilisable. On réessaiera à la prochaine ouverture.
      error: () => { this.referentialSubjects.set([]); this.referentialLoaded = false; },
    });
  }

  private messageFrom(err: unknown): string {
    const body = (err as { error?: { message?: string } })?.error;
    return body?.message || this.translate.instant('ACCESS.SAVE_ERROR');
  }
}

function sameSet(a: Set<string>, b: Set<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}
